import { DictionaryInputError, type DictionaryEntry, type Finding } from "./types.js";
import { hasWordCodePointAt, hasWordCodePointBefore, normalizeForMatch, normalizeWithIndex, originalSpan } from "./normalization.js";
import { rule } from "./rules.js";

const MAX_ENTRIES = 1_000;
const MAX_ALIASES_PER_ENTRY = 64;
const MAX_ALIAS_LENGTH = 256;

interface Pattern {
  value: string;
  entry: DictionaryEntry;
}
interface Node {
  next: Map<string, Node>;
  failure: Node;
  output: Pattern[];
}

function node(): Node {
  const result = { next: new Map<string, Node>(), failure: undefined as unknown as Node, output: [] as Pattern[] };
  result.failure = result;
  return result;
}

/** A small, dependency-free Aho–Corasick matcher for private dictionary aliases. */
class Automaton {
  private readonly root = node();

  constructor(patterns: readonly Pattern[]) {
    for (const pattern of patterns) this.insert(pattern);
    const queue: Node[] = [];
    for (const child of this.root.next.values()) {
      child.failure = this.root;
      queue.push(child);
    }
    for (let head = 0; head < queue.length; head += 1) {
      const current = queue[head]!;
      for (const [character, child] of current.next) {
        queue.push(child);
        let fallback = current.failure;
        while (fallback !== this.root && !fallback.next.has(character)) fallback = fallback.failure;
        child.failure = fallback.next.get(character) ?? this.root;
        child.output = child.output.concat(child.failure.output);
      }
    }
  }

  private insert(pattern: Pattern): void {
    let current = this.root;
    // Iterate code units, not code points: matcher positions must stay aligned
    // with JavaScript's UTF-16 string offsets.
    for (let index = 0; index < pattern.value.length; index += 1) {
      const character = pattern.value[index]!;
      let next = current.next.get(character);
      if (!next) {
        next = node();
        current.next.set(character, next);
      }
      current = next;
    }
    current.output.push(pattern);
  }

  match(text: string): Array<{ start: number; end: number; pattern: Pattern }> {
    const results: Array<{ start: number; end: number; pattern: Pattern }> = [];
    let current = this.root;
    for (let index = 0; index < text.length; index += 1) {
      const character = text[index]!;
      while (current !== this.root && !current.next.has(character)) current = current.failure;
      current = current.next.get(character) ?? this.root;
      for (const pattern of current.output) {
        results.push({ start: index + 1 - pattern.value.length, end: index + 1, pattern });
      }
    }
    return results;
  }
}

function patternsFor(entries: readonly DictionaryEntry[], caseSensitive: boolean): Pattern[] {
  const patterns: Pattern[] = [];
  for (const entry of entries) {
    if (Boolean(entry.caseSensitive) !== caseSensitive) continue;
    const values = [entry.canonicalValue, ...(entry.aliases ?? [])];
    for (const alias of values) {
      const value = normalizeForMatch(alias, caseSensitive);
      if (value.length > 0) patterns.push({ value, entry });
    }
  }
  return patterns;
}

export function validateDictionary(entries: readonly DictionaryEntry[]): void {
  if (entries.length > MAX_ENTRIES) throw new DictionaryInputError();
  const ids = new Set<string>();
  const aliases: Array<{ entryId: string; exact: string; folded: string; caseSensitive: boolean }> = [];
  for (const entry of entries) {
    if (!entry.id || ids.has(entry.id) || !entry.type || entry.canonicalValue.length === 0 || entry.canonicalValue.length > MAX_ALIAS_LENGTH) {
      throw new DictionaryInputError();
    }
    ids.add(entry.id);
    if ((entry.aliases?.length ?? 0) > MAX_ALIASES_PER_ENTRY || (entry.aliases ?? []).some((alias) => alias.length === 0 || alias.length > MAX_ALIAS_LENGTH)) {
      throw new DictionaryInputError();
    }
    for (const alias of [entry.canonicalValue, ...(entry.aliases ?? [])]) {
      const exact = normalizeForMatch(alias, true);
      const folded = normalizeForMatch(alias, false);
      const collision = aliases.some((seen) =>
        seen.entryId !== entry.id &&
        (seen.caseSensitive && entry.caseSensitive ? seen.exact === exact : seen.folded === folded),
      );
      if (collision) throw new DictionaryInputError();
      aliases.push({
        entryId: entry.id,
        exact,
        folded,
        caseSensitive: Boolean(entry.caseSensitive),
      });
    }
  }
}

export function findDictionaryFindings(input: string, entries: readonly DictionaryEntry[]): Finding[] {
  if (entries.length === 0) return [];
  validateDictionary(entries);
  const result: Finding[] = [];
  for (const caseSensitive of [false, true]) {
    const automaton = new Automaton(patternsFor(entries, caseSensitive));
    const normalized = normalizeWithIndex(input, caseSensitive);
    for (const match of automaton.match(normalized.value)) {
      const wholeWordOnly = match.pattern.entry.wholeWordOnly ?? true;
      if (wholeWordOnly && (
        hasWordCodePointBefore(normalized.value, match.start) ||
        hasWordCodePointAt(normalized.value, match.end)
      )) continue;
      const span = originalSpan(normalized, match.start, match.end);
      if (!span) continue;
      result.push({
        id: `private_dictionary@${rule("private_dictionary").version}:${match.pattern.entry.id}:${span.startUtf16}:${span.endUtf16}`,
        startUtf16: span.startUtf16,
        endUtf16: span.endUtf16,
        rawType: match.pattern.entry.type,
        displayType: match.pattern.entry.type,
        category: "dictionary",
        severity: "critical",
        confidence: 1,
        status: "validated",
        source: "dictionary",
        rule: rule("private_dictionary"),
        country: "core",
        validation: ["exact_normalized_alias"],
        dictionaryEntryId: match.pattern.entry.id,
      });
    }
  }
  // Canonical value may also appear in aliases. Surface one exact candidate,
  // not a duplicate suppressed candidate, for that intentionally identical hit.
  return [...new Map(result.map((finding) => [finding.id, finding])).values()];
}
